import React, { useState, useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Alert, Col, Row } from "react-bootstrap";
import WeeklyHours from "./WeeklyHours";
import LeadTime from "./LeadTime";
import { Availability, GetAvailabilityInput, GetAvailabilityOutput, SetAvailabilityInput, SetAvailabilityOutput } from "../../client/core";
import { Auth } from "aws-amplify";

const DEFAULT_AVAILABILITY: Availability = {
    weekly: [],
    overrides: [],
    leadTimeHours: 48
};

export interface CollectionsProps extends RouteComponentProps {
    shopId: string;
    onSave: (input: SetAvailabilityInput) => Promise<SetAvailabilityOutput | Error>;
    onGetAvailability: (input: GetAvailabilityInput) => Promise<GetAvailabilityOutput | Error>;
    auth: typeof Auth;
}

const Collections: React.FC<CollectionsProps> = ({ shopId, onSave, onGetAvailability, auth }) => {
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState<string>("");
    const [availability, setAvailability] = useState<Availability>(DEFAULT_AVAILABILITY);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchAvailability = async () => {
            setIsLoading(true);
            const response = await onGetAvailability({
                shopId,
                type: 'collection'
            });

            if (response instanceof Error) {
                setError("Failed to load availability. If this issue persists please contact: support@stumbled.online");
                setIsLoading(false);
                return;
            }

            if (response.body?.availability) {
                setAvailability(response.body.availability);
            }

            setIsLoading(false);
        };

        fetchAvailability();
    }, [shopId, onGetAvailability]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const handleAvailabilityChange = (newAvailability: Availability) => {
        setAvailability(newAvailability);
    };

    const handleWeeklyHoursSave = async (input: SetAvailabilityInput) => {
        setError("");
        setSuccess("");

        const session = await auth.currentSession();

        const response = await onSave({
            identityToken: session.getIdToken().getJwtToken(),
            availability: input.availability,
            shopId: input.shopId,
            type: input.type,
        });

        if (response instanceof Error) {
            setError("The weekly hours could not be updated at this time. If this issue persists please contact: support@stumbled.online");
        } else {
            setSuccess("Your changes have been saved.");
        }
    };

    return (
        <div>
            <Row>
                <Col>
                    <h1 style={{ marginBottom: "2rem" }}>Collections</h1>
                </Col>
            </Row>
            <Alert variant="danger" show={error !== ""}>
                {error}
            </Alert>
            <Alert variant="success" show={success !== ""}>
                {success}
            </Alert>
            <Row>
                <Col xs={12} lg={6} style={{ marginBottom: "2rem" }}>
                    {!isLoading && (
                        <WeeklyHours
                            shopId={shopId}
                            availability={availability}
                            onChange={handleAvailabilityChange}
                            onSave={handleWeeklyHoursSave}
                        />
                    )}
                </Col>
                <Col xs={12} lg={6} style={{ marginBottom: "2rem" }}>
                    {!isLoading && (
                        <LeadTime
                            shopId={shopId}
                            availability={availability}
                            onChange={handleAvailabilityChange}
                            onSave={handleWeeklyHoursSave}
                        />
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default Collections;
